/**
 * Carousel generation: dispatcher + poller for the async background job
 * workflow. POST /content-generation/carousel/generate returns HTTP 202 with
 * status="pending"; the final per-slide S3 URLs surface only via
 * GET /content-generation/carousel/status/{post_id}. This module keeps that
 * orchestration framework-agnostic so it can be unit-tested against a mocked
 * API client.
 */
import type {
  CarouselAlreadyGeneratedResponse,
  CarouselGenerateRequest,
  CarouselGenerateResponse,
  ImageOption,
} from '../types/carousel';

export const CAROUSEL_POLL_INTERVAL_MS = 5000;
// Slide 1 renders first (it is the style reference), then the other slides in
// parallel — about two GPT Image 2 renders back to back, plus quality-gate retries.
export const CAROUSEL_POLL_TIMEOUT_MS = 12 * 60 * 1000;
export const CAROUSEL_MAX_CONSECUTIVE_ERRORS = 3;

/** Minimal surface of the API client this service needs. */
export interface CarouselApiClient {
  generateCarousel(
    request: CarouselGenerateRequest
  ): Promise<CarouselGenerateResponse | CarouselAlreadyGeneratedResponse>;
  getCarouselStatus(postId: string): Promise<CarouselGenerateResponse>;
}

export interface DispatchResult {
  /** Resolved post_id the background job runs against. */
  postId: string;
  /** Raw POST response — status is expected to be "pending" on 202. */
  response: CarouselGenerateResponse | CarouselAlreadyGeneratedResponse;
}

/**
 * Dispatch the generation job. Resolves once the POST returns (202), i.e. the
 * job has been accepted and runs in the background — it does NOT wait for the
 * slides. Throws on transport/HTTP errors so callers can surface a message.
 */
export async function dispatchCarouselGeneration(
  api: CarouselApiClient,
  request: CarouselGenerateRequest
): Promise<DispatchResult> {
  const response = await api.generateCarousel(request);
  const postId = response && 'post_id' in response ? response.post_id : request.post_id;
  return { postId, response };
}

export interface PollCarouselStatusOptions {
  postId: string;
  /** Cadence between status checks. Defaults to 5s. */
  intervalMs?: number;
  /** Max wait before giving up and firing onTimeout. Defaults to 3 min. */
  timeoutMs?: number;
  /** Consecutive HTTP/network errors tolerated before onError. Defaults to 3. */
  maxConsecutiveErrors?: number;
  /** Fired on every successful status read (useful for debug/tests). */
  onStatus?: (status: CarouselGenerateResponse) => void;
  /**
   * status === "done" with non-empty output_paths (one URL per slide).
   * imageOptions/selectedImageIndex are set only when one slide's quality
   * gate exhausted every attempt — a caveat on an otherwise-done job, not
   * an error.
   */
  onDone: (imageUrls: string[], imageOptions?: ImageOption[] | null, selectedImageIndex?: number | null) => void;
  /** status === "error", a 404 (post missing), or too many consecutive polling errors. */
  onError: (message: string) => void;
  /** Polled for the full timeout window without a terminal state. */
  onTimeout: () => void;
}

/**
 * Poll the status endpoint until the job reaches a terminal state, the
 * timeout elapses, or cancellation. Returns a cancel() handle.
 *
 * Stopping semantics:
 * - done  -> onDone(urls), stop
 * - error -> onError(message), stop (credits already auto-refunded)
 * - timeout -> onTimeout(), stop (surfaces a "timed out, try again" notice;
 *   the job may still complete server-side)
 * - 404 (post missing) -> onError and stop immediately
 * - other network/HTTP -> up to maxConsecutiveErrors transient retries, then
 *   onError and stop (no infinite loop)
 */
export function pollCarouselStatus(
  api: CarouselApiClient,
  options: PollCarouselStatusOptions
): () => void {
  const {
    postId,
    intervalMs = CAROUSEL_POLL_INTERVAL_MS,
    timeoutMs = CAROUSEL_POLL_TIMEOUT_MS,
    maxConsecutiveErrors = CAROUSEL_MAX_CONSECUTIVE_ERRORS,
    onStatus,
    onDone,
    onError,
    onTimeout,
  } = options;

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let consecutiveErrors = 0;
  const startedAt = Date.now();

  const stop = () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const isExpired = () => Date.now() - startedAt >= timeoutMs;

  const scheduleNext = () => {
    if (cancelled) return;
    timer = setTimeout(() => { void tick(); }, intervalMs);
  };

  const tick = async () => {
    if (cancelled) return;
    if (isExpired()) {
      onTimeout();
      stop();
      return;
    }
    try {
      const status = await api.getCarouselStatus(postId);
      if (cancelled) return;
      consecutiveErrors = 0;
      onStatus?.(status);

      if (status.status === 'done') {
        const imageUrls = status.output_paths || [];
        if (imageUrls.length > 0) {
          onDone(imageUrls, status.image_options, status.selected_image_index);
        } else {
          onError('Generation completed, but no slide URLs were returned.');
        }
        stop();
        return;
      }

      if (status.status === 'error') {
        onError(status.error_message || 'Generation failed. Your credits were refunded automatically.');
        stop();
        return;
      }

      // status === "pending" — worker still running
      scheduleNext();
    } catch (err: any) {
      if (cancelled) return;
      // Post no longer exists — nothing to wait on, stop immediately.
      if (err?.response?.status === 404) {
        onError('This post could not be found. Generation could not be completed.');
        stop();
        return;
      }
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        onError(
          'Could not check the generation status. Please try again in a moment.'
        );
        stop();
        return;
      }
      scheduleNext();
    }
  };

  void tick();
  return stop;
}
